import React from "react";
import { Alert, Spinner, Table } from "react-bootstrap";
import api from "../../../services/httpClient";
import "../../../assets/styles/scheduledChanges.scss";

const columnAliases = {
  cityname: "CityName",
  postcode: "Postcode",
  streetname: "StreetName",
  terminaldr: "TerminalDr",
  terminalex: "TerminalEx",
  streetnumber: "StreetNumber",
  datefrom: "DateFrom",
  dateto: "DateTo",
  missingfields: "MissingFields",
  missingfieldsmatchall: "MissingFieldsMatchAll",
  type: "Type",
  buildingtype: "BuildingType",
  parity: "Parity",
  numberfrom: "NumberFrom",
  numberto: "NumberTo",
  postcodefrom: "PostcodeFrom",
  postcodeto: "PostcodeTo",
  zone: "Zone",
  region: "Region",
};

const parseIdsFromValue = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((v) => Number(v))
      .filter((v) => Number.isFinite(v));
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? [value] : [];
  }

  if (typeof value === "string") {
    const matches = value.match(/\d+/g) || [];
    return matches
      .map((v) => Number(v))
      .filter((v) => Number.isFinite(v));
  }

  if (value && typeof value === "object") {
    if (Array.isArray(value.ids)) {
      return value.ids
        .map((v) => Number(v))
        .filter((v) => Number.isFinite(v));
    }

    if ("value" in value) {
      return parseIdsFromValue(value.value);
    }
  }

  return [];
};

const extractIdsFromChangeDetails = (changeDetailsJson) => {
  if (!changeDetailsJson) return [];

  let parsed = changeDetailsJson;
  if (typeof changeDetailsJson === "string") {
    try {
      parsed = JSON.parse(changeDetailsJson);
    } catch {
      return [];
    }
  }

  const filters = parsed?.Filters ?? parsed?.filters;
  if (!Array.isArray(filters)) return [];

  const idFilter = filters.find(
    (f) => String(f?.Column ?? f?.column ?? "").toLowerCase() === "id"
  );

  if (!idFilter) return [];

  const rawIds = parseIdsFromValue(idFilter?.Value ?? idFilter?.value);
  const uniqueIds = [...new Set(rawIds.map((id) => Math.trunc(id)).filter((id) => id > 0))];
  return uniqueIds;
};

const toQueryName = (columnName) => {
  const raw = String(columnName ?? "").trim();
  if (!raw) return null;

  const canonical = columnAliases[raw.toLowerCase()];
  return canonical ?? raw;
};

const normalizeFilterValue = (value) => {
  if (value === null || value === undefined || value === "") return undefined;
  if (Array.isArray(value)) return value;
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const extractListQueryParamsFromChangeDetails = (changeDetailsJson) => {
  if (!changeDetailsJson) return {};

  let parsed = changeDetailsJson;
  if (typeof changeDetailsJson === "string") {
    try {
      parsed = JSON.parse(changeDetailsJson);
    } catch {
      return {};
    }
  }

  const filters = parsed?.Filters ?? parsed?.filters;
  if (!Array.isArray(filters)) return {};

  const params = {};
  filters.forEach((filter) => {
    const column = filter?.Column ?? filter?.column ?? filter?.Colunm ?? filter?.colunm;
    const queryName = toQueryName(column);
    if (!queryName || queryName === "Id") return;

    const rawValue = filter?.Value ?? filter?.value;
    const mappedValue = normalizeFilterValue(rawValue);
    if (mappedValue === undefined) return;

    params[queryName] = mappedValue;
  });

  return params;
};

const parseSearchArea = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    try {
      return JSON.parse(trimmed.replace(/\\/g, ""));
    } catch {
      return null;
    }
  }

  return null;
};

const extractSearchAreaAndListParams = (listQueryParams) => {
  const source =
    listQueryParams && typeof listQueryParams === "object"
      ? listQueryParams
      : {};

  const searchAreaKey = Object.keys(source).find(
    (key) => String(key).toLowerCase() === "searcharea",
  );

  const searchArea = searchAreaKey ? parseSearchArea(source[searchAreaKey]) : null;

  if (!searchAreaKey) {
    return { searchArea, listParamsWithoutSearchArea: source };
  }

  const { [searchAreaKey]: _ignored, ...rest } = source;
  return { searchArea, listParamsWithoutSearchArea: rest };
};

const ScheduledChangeDetailRecords = ({ changeDetailsJson }) => {
  const [rows, setRows] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const shouldEnableScroll = rows.length > 15;

  React.useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const ids = extractIdsFromChangeDetails(changeDetailsJson);
    const listQueryParams = extractListQueryParamsFromChangeDetails(changeDetailsJson);
    const { searchArea, listParamsWithoutSearchArea } =
      extractSearchAreaAndListParams(listQueryParams);
    const hasListFilters = Object.keys(listParamsWithoutSearchArea).length > 0;

    if (ids.length === 0 && !hasListFilters && !searchArea) {
      setRows([]);
      setError(null);
      setIsLoading(false);
      return () => {
        cancelled = true;
        controller.abort();
      };
    }

    const fetchAddresses = async () => {
      setIsLoading(true);
      setError(null);

      try {
        let response;
        if (searchArea) {
          response = await api.post(
            "/Address/ListByGeometry",
            {
              filter: listParamsWithoutSearchArea,
              pagination: {
                pageNumber: 1,
                pageSize: 200,
                sortBy: "Id",
                sortDirection: "asc",
              },
              searchArea,
            },
            {
              signal: controller.signal,
            },
          );
        } else if (ids.length > 0) {
          response = await api.get("/Address/ByIds", {
            signal: controller.signal,
            params: { ids: ids.join(",") },
          });
        } else {
          response = await api.get("/Address/List", {
            signal: controller.signal,
            params: {
              PageNumber: 1,
              PageSize: 200,
              ...listParamsWithoutSearchArea,
            },
          });
        }

        const fetchedItems = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.items)
          ? response.data.items
          : [];

        if (cancelled) return;

        const normalizedRows = fetchedItems.map((item, index) => ({
          count: index + 1,
          addressName:
            item?.addressName ??
            item?.name ??
            item?.address ??
            item?.street ??
            "-",
        }));

        setRows(normalizedRows);
      } catch (fetchError) {
        if (!cancelled) {
          setRows([]);
          setError("Nie udało się pobrać listy adresów.");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchAddresses();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [changeDetailsJson]);

  return (
    <div className="sc-detail-table-shell sc-detail-table-shell--records">
      <h5 className="sc-detail-table__section-title sc-detail-table__section-title--records">Rekordy</h5>

      {isLoading && (
        <div className="d-flex align-items-center gap-2 px-2 py-2 text-muted">
          <Spinner animation="border" size="sm" />
          <span>Ładowanie adresów…</span>
        </div>
      )}

      {error && (
        <Alert variant="danger" className="mb-2">
          {error}
        </Alert>
      )}

      {!isLoading && !error && (
        <div
          className={`sc-detail-table__scroll-wrap ${
            shouldEnableScroll ? "sc-detail-table__scroll-wrap--active" : ""
          }`}
        >
          <Table size="sm" className="sc-detail-table mb-0" responsive>
            <colgroup>
              <col className="sc-detail-table__count-col" />
              <col />
            </colgroup>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td className="sc-detail-table__value" colSpan={2}>
                    Brak danych.
                  </td>
                </tr>
              )}

              {rows.map((row) => (
                <tr key={`${row.count}-${row.addressName}`}>
                  <td className="sc-detail-table__value sc-detail-table__count">{row.count}</td>
                  <td className="sc-detail-table__value">
                    <span className="sc-detail-table__text">{row.addressName}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default ScheduledChangeDetailRecords;   